import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  useQuery, 
  getLocationEntries, 
  deleteAllLocationEntries,
  deleteContent,
  deleteEvent,
  removeLocationMembership 
} from 'wasp/client/operations';
import DefaultLayout from '../../admin/layout/DefaultLayout';
import { useAuth } from 'wasp/client/auth';
import { 
  FileText, 
  Calendar, 
  Users, 
  Trash2, 
  AlertTriangle,
  ChevronLeft,
  MoreHorizontal,
  Edit
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { EnhancedActionButton } from '../../components/ui/enhanced-page-layout';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '../../components/ui/tabs';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from '../../components/ui/card';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '../../components/ui/dropdown-menu';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../hooks/use-toast';
import LoadingSpinner from '../../admin/layout/LoadingSpinner';

const AdminLocationEntriesPage: React.FC = () => {
  const { locationId } = useParams<{ locationId: string }>();
  const { data: user, isLoading: isUserLoading } = useAuth();
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  
  const { data: entries, isLoading: isEntriesLoading, error, refetch } = useQuery(getLocationEntries, { locationId: locationId || '' }) as { 
    data: any, isLoading: boolean, error: any, refetch: () => void 
  };

  if (isUserLoading || isEntriesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user || !user.isAdmin) {
    return (
      <div className="p-8 text-center flex flex-col items-center justify-center min-h-[60vh]">
        <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
        <h1 className="text-2xl font-bold">Access Denied</h1>
        <p className="text-gray-600 mt-2">Only administrators can access this page.</p>
        <Link to="/" className="mt-4 text-blue-600 hover:underline">Return to Home</Link>
      </div>
    );
  }

  if (error || !entries) {
    return (
      <DefaultLayout user={user}>
        <div className="p-8 text-center text-red-600">
          Error loading entries: {error?.message || "Location not found"}
        </div>
      </DefaultLayout>
    );
  }

  const { content, events, memberships, location } = entries;

  const handleDeleteAll = async () => {
    if (!confirm("Are you sure you want to delete ALL content, events, and memberships for this location? This cannot be undone.")) {
      return;
    }
    
    setIsDeletingAll(true);
    try {
      await deleteAllLocationEntries({ locationId: location.id });
      toast({
        title: "Success",
        description: "All entries have been deleted.",
      });
      refetch();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to delete entries.",
        variant: "destructive",
      });
    } finally {
      setIsDeletingAll(false);
    }
  };

  const handleDeleteContent = async (id: string) => {
    if (!confirm("Delete this content?")) return;
    try {
      await deleteContent({ id });
      toast({ title: "Deleted", description: "Content removed successfully." });
      refetch();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleDeleteEvent = async (id: string) => {
    if (!confirm("Delete this event?")) return;
    try {
      await deleteEvent({ eventId: id });
      toast({ title: "Deleted", description: "Event removed successfully." });
      refetch();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleRemoveMembership = async (id: string) => {
    if (!confirm("Remove this member from the location?")) return;
    try {
      await removeLocationMembership({ id });
      toast({ title: "Removed", description: "Member removed successfully." });
      refetch();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  return (
    <DefaultLayout user={user}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Breadcrumb */}
        <div className="mb-6 flex items-center gap-2">
          <Link to="/admin/locations" className="text-sm text-blue-600 hover:underline flex items-center transition-colors">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Locations
          </Link>
        </div>

        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 bg-white dark:bg-sidebar-active p-6 rounded-lg border border-border shadow-sm">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white capitalize tracking-tight">
                {location.name} <span className="text-gray-400 font-normal">Entries</span>
              </h1>
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                {location.locationCode}
              </Badge>
            </div>
            <p className="text-gray-500 dark:text-gray-400 mt-2 font-medium">
              Manage entries associated with this location: {content.length} Content, {events.length} Events, {memberships.length} Citizens
            </p>
          </div>
          <div className="flex gap-3">
            <EnhancedActionButton
              variant="danger"
              onClick={handleDeleteAll}
              disabled={isDeletingAll}
              icon={Trash2}
              label="Delete All Entries"
              hideLabelOnMobile
            />
          </div>
        </div>

        {/* Tabs Section */}
        <Tabs defaultValue="content" className="w-full space-y-6">
          <TabsList className="grid w-full grid-cols-3 md:w-[600px] h-12 p-1 bg-gray-100/50 dark:bg-sidebar-active/50 rounded-lg">
            <TabsTrigger value="content" className="data-[state=active]:bg-white dark:data-[state=active]:bg-sidebar-hover data-[state=active]:shadow-sm rounded-md transition-all h-10">
              <FileText className="w-4 h-4 mr-2" />
              Content ({content.length})
            </TabsTrigger>
            <TabsTrigger value="events" className="data-[state=active]:bg-white dark:data-[state=active]:bg-sidebar-hover data-[state=active]:shadow-sm rounded-md transition-all h-10">
              <Calendar className="w-4 h-4 mr-2" />
              Events ({events.length})
            </TabsTrigger>
            <TabsTrigger value="members" className="data-[state=active]:bg-white dark:data-[state=active]:bg-sidebar-hover data-[state=active]:shadow-sm rounded-md transition-all h-10">
              <Users className="w-4 h-4 mr-2" />
              Citizens ({memberships.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="content" className="animate-in fade-in-50 duration-300">
            <Card className="border-border/60 shadow-md">
              <CardHeader className="bg-gray-50/50 dark:bg-sidebar-active/30 border-b border-border/60">
                <CardTitle className="text-xl">Associated Content</CardTitle>
                <CardDescription>All posts and records linked to this location.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 dark:bg-sidebar-active/50 text-gray-500 font-semibold uppercase tracking-wider text-xs">
                      <tr>
                        <th className="px-6 py-4">Title</th>
                        <th className="px-6 py-4">Author</th>
                        <th className="px-6 py-4">Type</th>
                        <th className="px-6 py-4">Status</th>
                        <th className="px-6 py-4">Created</th>
                        <th className="px-6 py-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {content.length > 0 ? content.map((item: any) => (
                        <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-sidebar-hover/40 transition-colors">
                          <td className="px-6 py-4 font-semibold text-gray-900 dark:text-gray-100">{item.title}</td>
                          <td className="px-6 py-4">{item.author?.fullName || item.author?.username || "Unknown"}</td>
                          <td className="px-6 py-4"><Badge variant="secondary">{item.contentType}</Badge></td>
                          <td className="px-6 py-4">
                            <Badge variant={item.isApproved ? "default" : "secondary"}>
                              {item.isApproved ? "Approved" : "Pending"}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 text-gray-500">{new Date(item.createdAt).toLocaleDateString()}</td>
                          <td className="px-6 py-4 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48 shadow-xl">
                                <DropdownMenuItem asChild>
                                  <Link to={`/admin/content/${item.id}`} className="cursor-pointer">
                                    <Edit className="w-4 h-4 mr-2" /> Edit/Moderate
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteContent(item.id)} className="text-red-600 focus:text-red-700 focus:bg-red-50 cursor-pointer">
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan={6} className="px-6 py-12 text-center text-gray-400 italic font-medium">No content found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="events" className="animate-in fade-in-50 duration-300">
            <Card className="border-border/60 shadow-md">
              <CardHeader className="bg-gray-50/50 dark:bg-sidebar-active/30 border-b border-border/60">
                <CardTitle className="text-xl">Associated Events</CardTitle>
                <CardDescription>Events scheduled to take place at or linked to this location.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 dark:bg-sidebar-active/50 text-gray-500 font-semibold uppercase tracking-wider text-xs">
                      <tr>
                        <th className="px-6 py-4">Event Name</th>
                        <th className="px-6 py-4">Organizer</th>
                        <th className="px-6 py-4">Start Time</th>
                        <th className="px-6 py-4">Status</th>
                        <th className="px-6 py-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {events.length > 0 ? events.map((event: any) => (
                        <tr key={event.id} className="hover:bg-gray-50 dark:hover:bg-sidebar-hover/40 transition-colors">
                          <td className="px-6 py-4 font-semibold text-gray-900 dark:text-gray-100">{event.name}</td>
                          <td className="px-6 py-4">{event.organizer?.fullName || event.organizer?.username || "Unknown"}</td>
                          <td className="px-6 py-4">{new Date(event.startTime).toLocaleString()}</td>
                          <td className="px-6 py-4">
                            <Badge variant={event.isApproved ? "default" : "secondary"}>
                              {event.isApproved ? "Approved" : "Pending"}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 text-right">
                             <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48 shadow-xl">
                                <DropdownMenuItem asChild>
                                  <Link to={`/admin/events/${event.id}`} className="cursor-pointer">
                                    <Edit className="w-4 h-4 mr-2" /> Edit/Moderate
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteEvent(event.id)} className="text-red-600 focus:text-red-700 focus:bg-red-50 cursor-pointer">
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan={5} className="px-6 py-12 text-center text-gray-400 italic font-medium">No events found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="members" className="animate-in fade-in-50 duration-300">
            <Card className="border-border/60 shadow-md">
              <CardHeader className="bg-gray-50/50 dark:bg-sidebar-active/30 border-b border-border/60">
                <CardTitle className="text-xl">Citizens</CardTitle>
                <CardDescription>Registered members/residents associated with this location.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 dark:bg-sidebar-active/50 text-gray-500 font-semibold uppercase tracking-wider text-xs">
                      <tr>
                        <th className="px-6 py-4">Name</th>
                        <th className="px-6 py-4">Email</th>
                        <th className="px-6 py-4">Joined At</th>
                        <th className="px-6 py-4">Role</th>
                        <th className="px-6 py-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {memberships.length > 0 ? memberships.map((membership: any) => (
                        <tr key={membership.id} className="hover:bg-gray-50 dark:hover:bg-sidebar-hover/40 transition-colors">
                          <td className="px-6 py-4 font-semibold text-gray-900 dark:text-gray-100">{membership.user?.fullName || membership.user?.username || "Unknown"}</td>
                          <td className="px-6 py-4">{membership.user?.email}</td>
                          <td className="px-6 py-4">{new Date(membership.joinedAt).toLocaleDateString()}</td>
                          <td className="px-6 py-4">
                            <Badge variant={membership.role === "OWNER" ? "default" : "secondary"}>{membership.role}</Badge>
                          </td>
                          <td className="px-6 py-4 text-right">
                             <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleRemoveMembership(membership.id)}
                            >
                               <Trash2 className="w-4 h-4 mr-2" />
                               Remove
                             </Button>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan={5} className="px-6 py-12 text-center text-gray-400 italic font-medium">No citizens found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DefaultLayout>
  );
};

export default AdminLocationEntriesPage;
